import {
    Box,
    TableCell,
    TableRow,
    Typography,
    useTheme,Grid
} from "@mui/material";
import { SxProps, Theme } from "@mui/system";
import { checkImageExists, formatCurrency } from "../../../utils/utilityFunctions";
import { SchemeDetail } from "../../../redux-store/types/products";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";

export default function MutualFundSchemeDetailBody(
    {
        tableBodyData,
        tableBodyRowCustomStyles = {},
    }: {
        tableBodyData: SchemeDetail[];
        tableBodyRowCustomStyles: SxProps<Theme>,
    }
): JSX.Element {
    const { palette } = useTheme()

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const amcIds = ['BDOTR', 'BPIAMC']



    return (
        <>
            {tableBodyData.map(({
                schemeName,
                investedValue,
                marketValue,
                categoryName,
                schemeLogo,
                amcId,
                folioNo,
                percentage: gainLossObj
            }, index: number) => {
                const roundedPerc = !Number.isNaN(Number(gainLossObj?.percentage)) ?
                    Math.round(gainLossObj.percentage * 100) / 100 : ''

                const checkAmcId = amcIds.includes(amcId ?? '') ? amcId?.toLowerCase() : amcId;

                return <TableRow
                    key={`${schemeName}-${index}`}
                    sx={{
                        '& td': {
                            paddingTop: "15px",
                            paddingBottom: "15px",
                            borderBottom: `0.5px solid ${palette.text.secondary}B2`,
                        },
                        ...tableBodyRowCustomStyles
                    }}
                >
                    <TableCell
                        align="left"
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                flexDirection: 'row'
                            }}
                        >
                            <img
                                src={checkImageExists(`${checkAmcId}`) ? `../../../../../images/amcIcons/${checkAmcId}.svg` : `../../../../../images/amcIcons/default.svg`}
                                alt={schemeName}
                                width={'35px'}
                                height={'35px'}
                                style={{ borderRadius: '8px' }}
                            />
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        color: theme.palette.primary[500]
                                    },
                                })}
                            >
                                {schemeName ? `${schemeName}(${folioNo})` : `-`}
                            </Typography>
                        </Box>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                color: palette.text.primary
                            }}
                        >
                            {categoryName}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                color: palette.text.primary
                            }}
                        >
                            {investedValue < 0 ? `- ${formatCurrency({ value: Math.abs(investedValue), locale, currencyCode })}` : `${formatCurrency({ value: investedValue, locale, currencyCode })}`}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                color: palette.text.primary
                            }}
                        >
                            {marketValue < 0 ? `- ${formatCurrency({ value: Math.abs(marketValue), locale, currencyCode })}` : `${formatCurrency({ value: marketValue, locale, currencyCode })}`}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={{
                                color: (Number(gainLossObj.amount) < 0 || Number(roundedPerc) < 0) ? palette.error.main : palette.success.main
                            }}
                        >
                            {gainLossObj.amount < 0 ? `- ${formatCurrency({ value: Math.abs(gainLossObj.amount), locale, currencyCode })}` : `${formatCurrency({ value: gainLossObj.amount, locale, currencyCode })}`} ({roundedPerc}%)
                        </Typography>
                    </TableCell>
                </TableRow>
            })}
        </>
    )
}

export  function MobileResponsiveBodySchemeDetail(
  {
    tableBodyData,
    tableHeaderData
  }: {
    tableBodyData: SchemeDetail[];
    tableHeaderData: Array<{ id: string; label: string }>;
  }
): JSX.Element {
  const { palette } = useTheme();
  const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
  const amcIds = ["BDOTR", "BPIAMC"];

  const getLabel = (id: string, fallback: string) => tableHeaderData.find((h: any) => h?.id === id)?.label || fallback;

  return (
    <>
      {tableBodyData.map(
        (
          {
            schemeName,
            investedValue,
            marketValue,
            categoryName,
            amcId,
            folioNo,
            percentage: gainLossObj,
          },
          index: number
        ) => {
          const roundedPerc = !Number.isNaN(Number(gainLossObj?.percentage))
            ? Math.round(gainLossObj.percentage * 100) / 100
            : "";

          const checkAmcId = amcIds.includes(amcId ?? "")
            ? amcId?.toLowerCase()
            : amcId;

          return (
            <Box
              key={`${schemeName}-${index}`}
              sx={{
                border: `0.5px solid ${palette.text.secondary}40`,
                borderRadius: "12px",
                p: 2,
                mb: 2,
                backgroundColor: palette.background.paper,
              }}
            >
              {/* Header Section */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  mb: 2,
                }}
              >
                <img
                  src={
                    checkImageExists(`${checkAmcId}`)
                      ? `../../../../../images/amcIcons/${checkAmcId}.svg`
                      : `../../../../../images/amcIcons/default.svg`
                  }
                  alt={schemeName}
                  width={"35px"}
                  height={"35px"}
                  style={{ borderRadius: "8px" }}
                />
                <Typography
                  variant="body_500"
                  sx={(theme: Theme) => ({
                    color: theme.palette.text.primary,
                    "&:hover": {
                      color: theme.palette.primary[500],
                    },
                  })}
                >
                  {schemeName ? `${schemeName} (${folioNo})` : `-`}
                </Typography>
              </Box>

              {/* Grid Section */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography
                    variant="body_500"
                    sx={{ color: palette.text.secondary, fontSize: "12px" }}
                  >
                    {getLabel('categoryName', 'Category')}
                  </Typography>
                  <Typography
                    variant="body_500"
                    sx={{ color: palette.text.primary }}
                  >
                    {categoryName}
                  </Typography>
                </Grid>

                <Grid item xs={6} sx={{ textAlign: "end" }}>
                  <Typography
                    variant="body_500"
                    sx={{ color: palette.text.secondary, fontSize: "12px" }}
                  >
                    {getLabel('investedValue', 'Invested')}
                  </Typography>
                  <Typography
                    variant="body_500"
                    sx={{ color: palette.text.primary }}
                  >
                    {investedValue < 0
                      ? `- ${formatCurrency({
                          value: Math.abs(investedValue),
                          locale,
                          currencyCode,
                        })}`
                      : `${formatCurrency({
                          value: investedValue,
                          locale,
                          currencyCode,
                        })}`}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography
                    variant="body_500"
                    sx={{ color: palette.text.secondary, fontSize: "12px" }}
                  >
                    {getLabel('marketValue', 'Market Value')}
                  </Typography>
                  <Typography
                    variant="body_500"
                    sx={{ color: palette.text.primary }}
                  >
                    {marketValue < 0
                      ? `- ${formatCurrency({
                          value: Math.abs(marketValue),
                          locale,
                          currencyCode,
                        })}`
                      : `${formatCurrency({
                          value: marketValue,
                          locale,
                          currencyCode,
                        })}`}
                  </Typography>
                </Grid>

                <Grid item xs={6} sx={{ textAlign: "end" }}>
                  <Typography
                    variant="body_500"
                    sx={{ color: palette.text.secondary, fontSize: "12px" }}
                  >
                    {getLabel('unrealisedAmount', 'Gain / Loss')}
                  </Typography>
                  <Typography
                    variant="body_500"
                    sx={{
                      color:
                        Number(gainLossObj.amount) < 0 || Number(roundedPerc) < 0
                          ? palette.error.main
                          : palette.success.main,
                    }}
                  >
                    {gainLossObj.amount < 0
                      ? `- ${formatCurrency({
                          value: Math.abs(gainLossObj.amount),
                          locale,
                          currencyCode,
                        })}`
                      : `${formatCurrency({
                          value: gainLossObj.amount,
                          locale,
                          currencyCode,
                        })}`} {`(${roundedPerc}%)`}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          );
        }
      )}
    </>
  );
}